
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

interface Product {
  id: number;
  name: string;
  category: string;
  categoryColor: string;
  description: string;
  image: string;
  price: string;
  priceLabel: string;
  rating: number;
  availability: string;
  availabilityColor: string;
  specs: { label: string; value: string }[];
}

const RoutersPage = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [compareCount, setCompareCount] = useState(0);
  const [comparisonVisible, setComparisonVisible] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  const products: Product[] = [
    {
      id: 1,
      name: 'Cisco ISR4321 Integrated Services Router',
      category: isNL ? 'Enterprise Router' : 'Enterprise Router',
      categoryColor: 'blue',
      description: isNL
        ? 'Enterprise-klasse router met geavanceerde beveiligingsfuncties, dubbele Gigabit Ethernet-poorten en modulaire uitbreidingsslots.'
        : 'Enterprise-grade router with advanced security features, dual Gigabit Ethernet ports, and modular expansion slots.',
      image:
        'https://public.readdy.ai/ai/img_res/4b02e5136e5387b3e660d1443ce5426f.jpg',
      price: '€1.199,99',
      priceLabel: isNL ? 'Adviesprijs' : 'MSRP',
      rating: 4.5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Doorvoer:' : 'Throughput:', value: '100 Mbps' },
        { label: isNL ? 'WAN-poorten:' : 'WAN Ports:', value: '2 x GE' },
        { label: isNL ? 'Geheugen:' : 'Memory:', value: '4GB DRAM' },
        { label: isNL ? 'Modules:' : 'Modules:', value: '2 x NIM Slots' },
      ],
    },
    {
      id: 2,
      name: 'Netgear GS724T 24-Port Smart Switch',
      category: isNL ? 'Beheerde Switch' : 'Managed Switch',
      categoryColor: 'green',
      description: isNL
        ? 'Slim beheerde switch met 24 Gigabit-poorten, VLAN-ondersteuning, QoS-functies en webgebaseerde beheerinterface.'
        : 'Smart managed switch with 24 Gigabit ports, VLAN support, QoS features, and web-based management interface.',
      image:
        'https://readdy.ai/api/search-image?query=Netgear%20GS724T%20managed%20network%20switch%20with%2024%20ethernet%20ports%20black%20chassis%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=netgear-gs724t-switch&orientation=landscape',
      price: '€299,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 4,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Poorten:' : 'Ports:', value: '24 x 1GbE' },
        { label: isNL ? 'Schakelcapaciteit:' : 'Switching Capacity:', value: '48 Gbps' },
        { label: isNL ? 'Beheer:' : 'Management:', value: isNL ? 'Webgebaseerd' : 'Web-based' },
        { label: isNL ? 'Functies:' : 'Features:', value: 'VLAN, QoS, LACP' },
      ],
    },
    {
      id: 3,
      name: 'TP-Link TL-SG1016PE 16-Port PoE+ Switch',
      category: isNL ? 'PoE Switch' : 'PoE Switch',
      categoryColor: 'purple',
      description: isNL
        ? "Onbeheerde PoE+ switch met 16 Gigabit-poorten, 110W PoE-budget voor IP-camera's en draadloze toegangspunten."
        : 'Unmanaged PoE+ switch with 16 Gigabit ports, 110W PoE budget for IP cameras and wireless access points.',
      image:
        'https://readdy.ai/api/search-image?query=TP-Link%20TL-SG1016PE%20PoE%20switch%20with%2016%20ethernet%20ports%20black%20chassis%20with%20green%20LED%20indicators%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=tplink-sg1016pe-poe&orientation=landscape',
      price: '€169,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 4.5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'PoE-poorten:' : 'PoE Ports:', value: '16 x PoE+' },
        { label: isNL ? 'PoE-budget:' : 'PoE Budget:', value: '110W' },
        { label: isNL ? 'Vermogen per poort:' : 'Per Port Power:', value: '30W max' },
        { label: isNL ? 'Beheer:' : 'Management:', value: isNL ? 'Onbeheerd' : 'Unmanaged' },
      ],
    },
    {
      id: 4,
      name: 'Ubiquiti UniFi AP AC Pro',
      category: isNL ? 'Draadloos Toegangspunt' : 'Wireless AP',
      categoryColor: 'orange',
      description: isNL
        ? 'Hoogwaardig dual-band 802.11ac toegangspunt met 1,75 Gbps doorvoer en naadloze roaming.'
        : 'High-performance dual-band 802.11ac access point with 1.75 Gbps throughput and seamless roaming.',
      image:
        'https://readdy.ai/api/search-image?query=Ubiquiti%20UniFi%20AP%20AC%20Pro%20wireless%20access%20point%20white%20circular%20design%20with%20blue%20LED%20ring%20ceiling%20mount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=ubiquiti-unifi-ac-pro&orientation=landscape',
      price: '€139,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'WiFi-standaard:' : 'WiFi Standard:', value: '802.11ac Wave 2' },
        { label: isNL ? 'Max. doorvoer:' : 'Max Throughput:', value: '1.75 Gbps' },
        { label: isNL ? 'Bereik:' : 'Coverage:', value: '183m (600ft)' },
        { label: isNL ? 'Voeding:' : 'Power:', value: isNL ? 'PoE+ Vereist' : 'PoE+ Required' },
      ],
    },
    {
      id: 5,
      name: 'Cisco Catalyst 9300-48P',
      category: isNL ? 'Laag 3 Switch' : 'Layer 3 Switch',
      categoryColor: 'red',
      description: isNL
        ? 'Enterprise Laag 3 switch met 48 PoE+-poorten, 4 x 10G SFP+ uplinks en geavanceerde beveiligingsfuncties.'
        : 'Enterprise Layer 3 switch with 48 PoE+ ports, 4 x 10G SFP+ uplinks, and advanced security features.',
      image:
        'https://readdy.ai/api/search-image?query=Cisco%20Catalyst%209300%20enterprise%20layer%203%20switch%20with%2048%20ports%20black%20chassis%20with%20blue%20accents%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=cisco-catalyst-9300&orientation=landscape',
      price: '€4.599,99',
      priceLabel: isNL ? 'Adviesprijs' : 'MSRP',
      rating: 4.5,
      availability: isNL ? 'Voorbestelling' : 'Pre-Order',
      availabilityColor: 'yellow',
      specs: [
        { label: isNL ? 'Toegangspoorten:' : 'Access Ports:', value: '48 x 1GbE PoE+' },
        { label: isNL ? 'Uplinks:' : 'Uplinks:', value: '4 x 10G SFP+' },
        { label: isNL ? 'PoE-budget:' : 'PoE Budget:', value: '740W' },
        { label: isNL ? 'Stacking:' : 'Stacking:', value: isNL ? 'Tot 8 eenheden' : 'Up to 8 units' },
      ],
    },
    {
      id: 6,
      name: 'Juniper SRX320 Services Gateway',
      category: isNL ? 'Enterprise Router' : 'Enterprise Router',
      categoryColor: 'blue',
      description: isNL
        ? 'Compacte beveiligingsgateway met geïntegreerde firewall, VPN en routeringsmogelijkheden voor filialen.'
        : 'Compact security gateway with integrated firewall, VPN, and routing capabilities for branch offices.',
      image:
        'https://readdy.ai/api/search-image?query=Juniper%20SRX320%20security%20gateway%20router%20black%20chassis%20with%20green%20LED%20indicators%20front%20view%20showing%20ethernet%20ports%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=juniper-srx320-gateway&orientation=landscape',
      price: '€829,99',
      priceLabel: isNL ? 'Adviesprijs' : 'MSRP',
      rating: 4,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Doorvoer:' : 'Throughput:', value: '1 Gbps' },
        { label: isNL ? 'Interfaces:' : 'Interfaces:', value: '8 x GE' },
        { label: isNL ? 'VPN-tunnels:' : 'VPN Tunnels:', value: '100 IPSec' },
        { label: isNL ? 'Beveiliging:' : 'Security:', value: 'IPS, UTM' },
      ],
    },
  ];

  const toggleCompare = (productId: number) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter((id) => id !== productId));
      setCompareCount((c) => c - 1);
    } else if (compareCount < 4) {
      setSelectedProducts([...selectedProducts, productId]);
      setCompareCount((c) => c + 1);
    }

    // Ensure the comparison bar visibility reflects the new state
    const newCount = selectedProducts.includes(productId)
      ? compareCount - 1
      : compareCount + 1;
    setComparisonVisible(newCount > 0);
  };

  const clearComparison = () => {
    setSelectedProducts([]);
    setCompareCount(0);
    setComparisonVisible(false);
  };

  const getCategoryColorClass = (color: string) => {
    const colors: { [key: string]: string } = {
      blue: 'text-blue-600 bg-blue-50',
      green: 'text-green-600 bg-green-50',
      purple: 'text-purple-600 bg-purple-50',
      orange: 'text-orange-600 bg-orange-50',
      red: 'text-red-600 bg-red-50',
      yellow: 'text-yellow-800 bg-yellow-100',
    };
    return colors[color] || 'text-gray-600 bg-gray-50';
  };

  const getAvailabilityColorClass = (color: string) => {
    const colors: { [key: string]: string } = {
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      red: 'bg-red-100 text-red-800',
    };
    return colors[color] || 'bg-gray-100 text-gray-800';
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div key={`full-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-fill"></i>
        </div>,
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-half-fill"></i>
        </div>,
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div key={`empty-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-line"></i>
        </div>,
      );
    }

    return stars;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-primary flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary">
              Hardware
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href={`${prefix}/products/networking`} className="text-gray-600 hover:text-primary">
              {isNL ? 'Netwerkapparatuur' : 'Networking'}
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">
              {isNL ? 'Routers & Switches' : 'Routers & Switches'}
            </span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start">
            <div className="max-w-4xl">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isNL ? 'Netwerkapparatuur - Routers & Switches' : 'Networking Equipment - Routers & Switches'}
              </h1>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Enterprise-klasse netwerkapparatuur inclusief routers, beheerde switches, PoE-switches en draadloze toegangspunten van toonaangevende fabrikanten.'
                  : 'Enterprise-grade networking equipment including routers, managed switches, PoE switches, and wireless access points from leading manufacturers.'}
              </p>

              {/* Categories Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Apparatuurcategorieën' : 'Equipment Categories'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-router-line text-primary text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Enterprise Routers' : 'Enterprise Routers'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-device-line text-green-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Beheerde Switches' : 'Managed Switches'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-plug-line text-blue-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'PoE Switches' : 'PoE Switches'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-wifi-line text-purple-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Draadloze Toegangspunten' : 'Wireless Access Points'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Toonaangevende Merken' : 'Leading Brands'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-building-line text-[#1BA0D7] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Cisco</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-router-line text-[#0080FF] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Netgear</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-device-line text-[#00A0E4] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">TP-Link</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-wifi-line text-[#0559C4] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Ubiquiti</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button className="px-4 py-2 bg-primary text-white rounded-button whitespace-nowrap text-sm font-medium hover:bg-primary/90 cursor-pointer">
                  {isNL ? 'Alle Apparatuur' : 'All Equipment'}
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200 cursor-pointer">
                  {isNL ? 'Enterprise Routers' : 'Enterprise Routers'}
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200 cursor-pointer">
                  {isNL ? 'Beheerde Switches' : 'Managed Switches'}
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200 cursor-pointer">
                  {isNL ? 'PoE Switches' : 'PoE Switches'}
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200 cursor-pointer">
                  {isNL ? 'Draadloze Toegangspunten' : 'Wireless Access Points'}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                className="flex items-center text-gray-600 hover:text-primary cursor-pointer"
                onClick={() => setComparisonVisible(!comparisonVisible)}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-scales-3-line"></i>
                </div>
                {isNL ? 'Vergelijken' : 'Compare'} (<span>{compareCount}</span>)
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                <button className="p-2 text-primary bg-blue-50 rounded cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </div>
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-list-check"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-20">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                <button className="text-primary text-sm hover:underline cursor-pointer">
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
              </div>

              {/* Equipment Type */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'Apparatuurtype' : 'Equipment Type'}
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">{isNL ? 'Enterprise Routers' : 'Enterprise Routers'}</span>
                  </label>
                  {/* Additional filter options omitted for brevity */}
                </div>
              </div>

              {/* Other filter sections omitted for brevity */}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {isNL ? (
                    <>
                      Toont <span>24</span> van 156 netwerkproducten
                    </>
                  ) : (
                    <>
                      Showing <span>24</span> of 156 networking products
                    </>
                  )}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8 cursor-pointer">
                    <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                    <option value="price-low">{isNL ? 'Prijs: Laag naar Hoog' : 'Price: Low to High'}</option>
                    <option value="price-high">{isNL ? 'Prijs: Hoog naar Laag' : 'Price: High to Low'}</option>
                    <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Toon:' : 'Show:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8 cursor-pointer">
                    <option value="12">12</option>
                    <option value="24" selected>
                      24
                    </option>
                    <option value="48">48</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow product-card"
                >
                  <div className="relative">
                    <div className="h-48 overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain bg-white" />
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getAvailabilityColorClass(product.availabilityColor)}`}>
                        {product.availability}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <input
                        type="checkbox"
                        className="compare-checkbox w-5 h-5 cursor-pointer"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleCompare(product.id)}
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColorClass(product.categoryColor)}`}>
                        {product.category}
                      </span>
                      <div className="text-yellow-400 flex">{renderStars(product.rating)}</div>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{product.description}</p>
                    <div className="space-y-2 mb-4">
                      {product.specs.map((spec, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600">{spec.label}</span>
                          <span className="font-medium">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs text-gray-500">{product.availability}</div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="bg-gray-100 text-gray-600 px-3 py-2 rounded-button whitespace-nowrap text-sm hover:bg-gray-200 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <a href={`${prefix}/contact`} className="bg-primary text-white px-3 py-2 rounded-button whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer">
                          {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-12">
              <div className="text-gray-600">{isNL ? '1-24 van 156 resultaten weergegeven' : 'Showing 1-24 of 156 results'}</div>
              <div className="flex items-center space-x-2">
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer" disabled>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                </button>
                <button className="px-3 py-2 bg-primary text-white rounded cursor-pointer">1</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">2</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">3</button>
                <span className="px-2 text-gray-500">...</span>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">7</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Bar */}
      {comparisonVisible && compareCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transition-transform duration-300 z-40">
          <div className="container mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <span className="font-medium">
                  {isNL ? 'Apparatuur Vergelijken' : 'Compare Equipment'} (<span>{compareCount}</span>/4)
                </span>
                <div className="flex space-x-2">
                  {selectedProducts.map((id) => {
                    const product = products.find((p) => p.id === id);
                    return product ? (
                      <div key={id} className="bg-gray-100 px-3 py-1 rounded text-sm">
                        {product.name.split(' ').slice(0, 2).join(' ')}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button className="text-gray-600 hover:text-gray-800 cursor-pointer" onClick={clearComparison}>
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
                <button className="bg-primary text-white px-6 py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                  {isNL ? 'Selectie Vergelijken' : 'Compare Selected'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default RoutersPage;
